import './styles.css';
import { animate, createTimeline, stagger } from 'animejs';
import { zipSync, strToU8 } from 'fflate';

type Relationship = { id: string; type: string; attributes?: Record<string, any> };
type Manga = { id: string; attributes: Record<string, any>; relationships: Relationship[] };
type Chapter = { id: string; attributes: Record<string, any>; relationships: Relationship[] };
type ApiList<T> = { data: T[]; total: number; limit: number; offset: number };
type Quality = 'data' | 'data-saver';
type JobState = 'queued' | 'resolving' | 'downloading' | 'packing' | 'complete' | 'failed' | 'cancelled';
type Job = { chapter: Chapter; state: JobState; done: number; total: number; message: string; controller: AbortController | null };
type Batch = { jobs: Job[]; running: boolean; quality: Quality; title: string };

const app = document.querySelector<HTMLDivElement>('#app')!;
if (!app) throw new Error('ChapterBox could not find #app.');

const state = {
  manga: null as Manga | null,
  rawChapters: [] as Chapter[],
  chapters: [] as Chapter[],
  selected: new Set<string>(),
  language: 'en',
  quality: 'data-saver' as Quality,
  totalRaw: 0,
  apiOffset: 0,
  loading: false,
  batch: null as Batch | null
};

const languages: Record<string, string> = {
  en: 'English', es: 'Spanish', fr: 'French', de: 'German', it: 'Italian', pt: 'Portuguese',
  'pt-br': 'Portuguese (BR)', ja: 'Japanese', ko: 'Korean', zh: 'Chinese', 'zh-hk': 'Chinese (Traditional)',
  ru: 'Russian', id: 'Indonesian', tr: 'Turkish', pl: 'Polish', vi: 'Vietnamese'
};

const esc = (value: unknown): string => String(value ?? '').replace(/[&<>'"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[c]!));
const rel = (item: Manga | Chapter, type: string): Relationship | undefined => item.relationships.find(r => r.type === type);
const titleOf = (m: Manga): string => m.attributes.title?.en || Object.values(m.attributes.title || {})[0] as string || 'Untitled manga';
const descriptionOf = (m: Manga): string => m.attributes.description?.en || Object.values(m.attributes.description || {})[0] as string || 'No description available.';
const groupName = (ch: Chapter): string => rel(ch, 'scanlation_group')?.attributes?.name || 'No group';
const coverUrl = (m: Manga, size = 512): string => {
  const file = rel(m, 'cover_art')?.attributes?.fileName;
  return file ? `https://uploads.mangadex.org/covers/${m.id}/${file}.${size}.jpg` : '/icons/icon.svg';
};
const cleanFile = (value: string): string => value.replace(/[<>:"/\\|?*\x00-\x1F]/g, '').replace(/\s+/g, ' ').trim().slice(0, 150) || 'chapter';
const reducedMotion = (): boolean => matchMedia('(prefers-reduced-motion: reduce)').matches;
const nextFrame = (): Promise<void> => new Promise(resolve => requestAnimationFrame(() => resolve()));

function chapterLabel(ch: Chapter): string {
  const a = ch.attributes;
  return `${a.volume ? `Vol. ${a.volume} · ` : ''}Ch. ${a.chapter || 'Oneshot'}`;
}

function chapterKey(ch: Chapter): string {
  const volume = String(ch.attributes.volume ?? '').trim().toLowerCase();
  const chapter = String(ch.attributes.chapter ?? 'oneshot').trim().toLowerCase();
  return `${volume}|${chapter}`;
}

function numericPart(value: unknown, fallback: number): number {
  const match = String(value ?? '').match(/-?\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : fallback;
}

function chapterScore(ch: Chapter): number {
  const a = ch.attributes;
  const date = Date.parse(a.updatedAt || a.publishAt || a.createdAt || '') || 0;
  const pages = Number(a.pages || 0);
  const hasGroup = rel(ch, 'scanlation_group') ? 1 : 0;
  return date + pages * 1000 + hasGroup * 100;
}

function dedupeAndSort(chapters: Chapter[]): Chapter[] {
  const best = new Map<string, Chapter>();
  for (const chapter of chapters) {
    const key = chapterKey(chapter);
    const existing = best.get(key);
    if (!existing || chapterScore(chapter) > chapterScore(existing)) best.set(key, chapter);
  }
  return [...best.values()].sort((a, b) => {
    const vol = numericPart(b.attributes.volume, -1) - numericPart(a.attributes.volume, -1);
    if (vol) return vol;
    const chapter = numericPart(b.attributes.chapter, -1) - numericPart(a.attributes.chapter, -1);
    if (chapter) return chapter;
    return Date.parse(b.attributes.publishAt || '') - Date.parse(a.attributes.publishAt || '');
  });
}

async function getJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(url, { signal, cache: 'no-store' });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body?.error || body?.errors?.[0]?.detail || `Request failed (${response.status})`);
  return body as T;
}

function shell(): void {
  app.innerHTML = `
    <header class="topbar">
      <button class="brand" type="button" data-action="home" aria-label="ChapterBox home">
        <span class="brand-cube"><i></i><b>↓</b></span><span>ChapterBox</span>
      </button>
      <nav class="top-actions">
        <span class="status-pill"><i></i> Live connection</span>
        <button class="icon-button" type="button" data-action="theme" aria-label="Toggle light and dark mode"><span aria-hidden="true">◐</span></button>
        <button class="queue-button" type="button" data-action="queue">Queue <span class="queue-count">0</span></button>
      </nav>
    </header>
    <main id="view"></main>
    <aside id="drawer" class="drawer" aria-hidden="true"></aside>
    <button id="backdrop" class="backdrop" type="button" data-action="close-drawer" hidden aria-label="Close panel"></button>
    <div id="toast-region" class="toast-region" aria-live="polite"></div>`;
}

function renderHome(): void {
  state.manga = null;
  state.rawChapters = [];
  state.chapters = [];
  state.selected.clear();
  const view = document.querySelector<HTMLElement>('#view')!;
  view.innerHTML = `
    <section class="hero">
      <div class="hero-copy-block">
        <div class="eyebrow reveal">A fluid MangaDex workspace</div>
        <h1 class="reveal">Find it.<br><span>Flow through it.</span></h1>
        <p class="lede reveal">A polished chapter workspace for finding titles, curating clean chapter sets, and exporting beautiful offline-ready bundles.</p>
        <form id="search-form" class="search-card reveal">
          <span class="search-glyph">⌕</span>
          <input id="search" autocomplete="off" placeholder="Search a title or paste a MangaDex URL" aria-label="Search MangaDex" />
          <button class="primary" type="submit">Search</button>
        </form>
        <div class="trust-row reveal"><span>Private local packaging</span><span>Fluid batch exports</span><span>Clean chapter matching</span></div>
      </div>
      <div class="hero-visual reveal" aria-hidden="true">
        <div class="orbit orbit-one"></div><div class="orbit orbit-two"></div>
        <div class="visual-card card-back"><span>CBZ</span></div>
        <div class="visual-card card-front"><div class="fake-cover"></div><strong>Fluid chapter flow</strong><small>Curate · package · read</small><i></i></div>
      </div>
      <div id="results" class="results"></div>
    </section>`;
  animateEntrance('.reveal');
}

async function submitSearch(): Promise<void> {
  const input = document.querySelector<HTMLInputElement>('#search');
  const results = document.querySelector<HTMLElement>('#results');
  if (!input || !results) return;
  const query = input.value.trim();
  if (!query) return shake(input.closest('.search-card') as HTMLElement);
  const id = query.match(/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i)?.[0];
  if (id) return void openManga(id);
  results.innerHTML = loadingCard('Searching MangaDex…');
  try {
    const data = await getJson<ApiList<Manga>>(`/api/search?q=${encodeURIComponent(query)}`);
    results.innerHTML = data.data.length ? `
      <div class="section-heading"><div><span class="eyebrow">Search results</span><h2>Choose a title</h2></div><span>${data.data.length} found</span></div>
      <div class="result-grid">${data.data.map(m => `
        <button class="manga-card" type="button" data-action="open-manga" data-id="${m.id}">
          <img src="${esc(coverUrl(m, 256))}" alt="" loading="lazy" />
          <span class="card-copy"><strong>${esc(titleOf(m))}</strong><small>${esc(m.attributes.year || 'Year unknown')} · ${esc(m.attributes.status || 'unknown')}</small></span>
          <span class="arrow">→</span>
        </button>`).join('')}</div>` : emptyCard('No matching titles found.');
    animateEntrance('.manga-card', 38);
  } catch (error) {
    results.innerHTML = errorCard((error as Error).message);
  }
}

async function openManga(id: string): Promise<void> {
  const view = document.querySelector<HTMLElement>('#view')!;
  view.innerHTML = `<div class="page-loader">${spinner()}<strong>Opening title</strong><small>Fetching details and chapter feed…</small></div>`;
  try {
    const manga = await getJson<{ data: Manga }>(`/api/manga/${id}`);
    state.manga = manga.data;
    state.rawChapters = [];
    state.chapters = [];
    state.selected.clear();
    state.apiOffset = 0;
    state.totalRaw = 0;
    await loadChapters(true);
    renderWorkspace();
  } catch (error) {
    view.innerHTML = `<div class="fatal-panel"><span>!</span><h2>Could not open this title</h2><p>${esc((error as Error).message)}</p><button class="primary" type="button" data-action="home">Back to search</button></div>`;
  }
}

async function loadChapters(reset = false): Promise<void> {
  if (!state.manga || state.loading) return;
  state.loading = true;
  try {
    if (reset) {
      state.rawChapters = [];
      state.apiOffset = 0;
    }
    const data = await getJson<ApiList<Chapter>>(`/api/manga/${state.manga.id}/chapters?language=${encodeURIComponent(state.language)}&offset=${state.apiOffset}`);
    state.rawChapters.push(...data.data);
    state.apiOffset += data.data.length;
    state.totalRaw = data.total;
    state.chapters = dedupeAndSort(state.rawChapters);
    const validIds = new Set(state.chapters.map(c => c.id));
    state.selected = new Set([...state.selected].filter(id => validIds.has(id)));
  } finally {
    state.loading = false;
  }
}

function renderWorkspace(): void {
  const manga = state.manga!;
  const author = rel(manga, 'author')?.attributes?.name || 'Unknown author';
  const removed = Math.max(0, state.rawChapters.length - state.chapters.length);
  const view = document.querySelector<HTMLElement>('#view')!;
  view.innerHTML = `
    <section class="workspace">
      <aside class="title-sidebar reveal">
        <button class="back-link" type="button" data-action="home">← Back to search</button>
        <div class="cover-shell"><img class="cover" src="${esc(coverUrl(manga))}" alt="Cover of ${esc(titleOf(manga))}" /><span>${esc(manga.attributes.status || 'unknown')}</span></div>
        <div class="title-info"><span class="eyebrow">${esc(manga.attributes.year || 'Year unknown')}</span><h1>${esc(titleOf(manga))}</h1><p class="author">${esc(author)}</p><p>${esc(descriptionOf(manga))}</p></div>
      </aside>
      <section class="chapter-area reveal">
        <div class="chapter-toolbar">
          <div><span class="eyebrow">Chapter library</span><h2>${state.chapters.length} unique chapters</h2><p>${removed ? `${removed} duplicate entr${removed === 1 ? 'y' : 'ies'} hidden from the loaded results.` : 'One entry is shown for each chapter number.'}</p></div>
          <div class="toolbar-actions">
            <label class="select-wrap"><span>Language</span><select id="language-select">${Object.entries(languages).map(([key, value]) => `<option value="${key}" ${key === state.language ? 'selected' : ''}>${esc(value)}</option>`).join('')}</select></label>
            <button class="secondary" type="button" data-action="select-all">${state.selected.size === state.chapters.length && state.chapters.length ? 'Clear all' : 'Select loaded'}</button>
          </div>
        </div>
        <div class="chapter-list">${chapterRows()}</div>
        ${state.apiOffset < state.totalRaw ? `<button class="load-more" type="button" data-action="load-more"><span>Load more chapters</span><small>${state.apiOffset}/${state.totalRaw} feed entries checked</small></button>` : ''}
      </section>
    </section>
    <div class="selection-dock ${state.selected.size ? 'show' : ''}"><div><span class="selection-number">${state.selected.size}</span><p><strong>chapter${state.selected.size === 1 ? '' : 's'} selected</strong><small>${state.selected.size > 1 ? 'They will be placed inside one ZIP.' : 'This will download as a CBZ.'}</small></p></div><button class="primary" type="button" data-action="download-options">Continue</button></div>`;
  animateEntrance('.reveal', 70);
  animateEntrance('.chapter-row', 16);
}

function chapterRows(): string {
  if (!state.chapters.length) return emptyCard('No downloadable chapters were returned for this language.');
  return state.chapters.map(chapter => {
    const selected = state.selected.has(chapter.id);
    const a = chapter.attributes;
    return `<button class="chapter-row ${selected ? 'selected' : ''}" type="button" data-action="toggle-chapter" data-id="${chapter.id}" aria-pressed="${selected}">
      <span class="checkbox">${selected ? '✓' : ''}</span>
      <span class="chapter-number"><strong>${esc(chapterLabel(chapter))}</strong><small>${esc(a.title || 'Untitled chapter')}</small></span>
      <span class="chapter-group"><strong>${esc(groupName(chapter))}</strong><small>${a.pages ? `${esc(a.pages)} pages · ` : ''}${esc(formatDate(a.publishAt))}</small></span>
      <span class="row-arrow">›</span>
    </button>`;
  }).join('');
}

function formatDate(value: string): string {
  try { return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(value)); } catch { return ''; }
}

function toggleChapter(id: string): void {
  state.selected.has(id) ? state.selected.delete(id) : state.selected.add(id);
  renderWorkspace();
  const row = document.querySelector<HTMLElement>(`[data-id="${CSS.escape(id)}"]`);
  if (row && !reducedMotion()) animate(row, { scale: [0.985, 1], duration: 240, ease: 'out(3)' });
}

function openDownloadOptions(): void {
  if (!state.selected.size) return toast('Select at least one chapter.');
  const count = state.selected.size;
  const drawer = document.querySelector<HTMLElement>('#drawer')!;
  drawer.innerHTML = `
    <div class="drawer-head"><div><span class="eyebrow">Export settings</span><h2>${count} chapter${count === 1 ? '' : 's'}</h2></div><button class="icon-button" type="button" data-action="close-drawer">×</button></div>
    <div class="option-card"><span class="option-label">Image quality</span>
      <button class="quality-choice ${state.quality === 'data' ? 'active' : ''}" type="button" data-action="quality" data-quality="data"><span><strong>Original quality</strong><small>Largest file, best available images</small></span><i></i></button>
      <button class="quality-choice ${state.quality === 'data-saver' ? 'active' : ''}" type="button" data-action="quality" data-quality="data-saver"><span><strong>Data saver</strong><small>Smaller download and lower memory use</small></span><i></i></button>
    </div>
    <div class="export-summary"><span>${count > 1 ? 'ZIP' : 'CBZ'}</span><div><strong>${count > 1 ? 'One bundle download' : 'One comic archive'}</strong><small>${count > 1 ? `Contains ${count} individually named CBZ files` : 'Ready for compatible comic readers'}</small></div></div>
    <button class="primary full" type="button" data-action="start-download">Start export</button>`;
  openDrawer();
}

function startBatch(): void {
  const manga = state.manga;
  if (!manga) return;
  const chapters = state.chapters.filter(ch => state.selected.has(ch.id));
  if (!chapters.length) return;
  state.batch = {
    title: titleOf(manga),
    quality: state.quality,
    running: false,
    jobs: chapters.map(chapter => ({ chapter, state: 'queued', done: 0, total: Number(chapter.attributes.pages || 0), message: 'Waiting', controller: null }))
  };
  state.selected.clear();
  closeDrawer(true);
  renderWorkspace();
  renderQueue();
  openDrawer();
  void runBatch();
}

async function runBatch(): Promise<void> {
  const batch = state.batch;
  if (!batch || batch.running) return;
  batch.running = true;
  const finished: Record<string, Uint8Array> = {};
  try {
    for (const job of batch.jobs) {
      if (job.state === 'cancelled') continue;
      try {
        const cbz = await buildCbz(job, batch.quality);
        finished[`${cleanFile(`${batch.title} - ${chapterLabel(job.chapter)}`)}.cbz`] = cbz;
        setJob(job, 'complete', 'Ready');
      } catch (error) {
        const cancelled = job.controller?.signal.aborted;
        setJob(job, cancelled ? 'cancelled' : 'failed', cancelled ? 'Cancelled' : (error as Error).message);
      }
    }
    const successful = Object.keys(finished);
    if (!successful.length) return toast('No chapters completed successfully.');
    await nextFrame();
    if (successful.length === 1 && batch.jobs.length === 1) {
      const filename = successful[0];
      saveBlob(new Blob([finished[filename].slice().buffer as ArrayBuffer], { type: 'application/vnd.comicbook+zip' }), filename);
      toast('CBZ download created.');
    } else {
      const bundle = zipSync(finished, { level: 0 });
      saveBlob(new Blob([bundle.slice().buffer as ArrayBuffer], { type: 'application/zip' }), `${cleanFile(batch.title)} - ${successful.length} chapters.zip`);
      toast(`ZIP bundle created with ${successful.length} chapter${successful.length === 1 ? '' : 's'}.`);
    }
  } finally {
    batch.running = false;
    renderQueueIfOpen();
    updateQueueCount();
  }
}

async function buildCbz(job: Job, quality: Quality): Promise<Uint8Array> {
  job.controller = new AbortController();
  setJob(job, 'resolving', 'Resolving chapter');
  const manifest = await getJson<any>(`/api/chapter/${job.chapter.id}/manifest?t=${Date.now()}`, job.controller.signal);
  const files: string[] = quality === 'data' ? manifest.chapter?.data : manifest.chapter?.dataSaver;
  if (!Array.isArray(files) || !files.length) throw new Error('No chapter pages were returned.');
  job.total = files.length;
  const archive: Record<string, Uint8Array> = {};
  const width = Math.max(3, String(files.length).length);
  for (let index = 0; index < files.length; index++) {
    setJob(job, 'downloading', `Page ${index + 1} of ${files.length}`);
    const response = await fetch(`/api/chapter/${job.chapter.id}/page?quality=${quality}&index=${index}&t=${Date.now()}`, { signal: job.controller.signal, cache: 'no-store' });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body?.error || `Page ${index + 1} failed (${response.status})`);
    }
    const source = files[index];
    const ext = source.split('.').pop()?.toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
    archive[`${String(index + 1).padStart(width, '0')}.${ext}`] = new Uint8Array(await response.arrayBuffer());
    job.done = index + 1;
    renderQueueIfOpen();
  }
  archive['ComicInfo.xml'] = strToU8(comicInfo(job.chapter));
  setJob(job, 'packing', 'Packing CBZ');
  await nextFrame();
  return zipSync(archive, { level: 0 });
}

function comicInfo(chapter: Chapter): string {
  const xml = (value: unknown) => esc(String(value ?? ''));
  return `<?xml version="1.0" encoding="utf-8"?><ComicInfo><Series>${xml(state.manga ? titleOf(state.manga) : '')}</Series><Number>${xml(chapter.attributes.chapter)}</Number><Volume>${xml(chapter.attributes.volume)}</Volume><Title>${xml(chapter.attributes.title)}</Title><Writer>${xml(state.manga ? rel(state.manga, 'author')?.attributes?.name : '')}</Writer><LanguageISO>${xml(chapter.attributes.translatedLanguage)}</LanguageISO><Publisher>MangaDex</Publisher></ComicInfo>`;
}

function setJob(job: Job, stateValue: JobState, message: string): void {
  job.state = stateValue;
  job.message = message;
  renderQueueIfOpen();
  updateQueueCount();
}

function renderQueueIfOpen(): void {
  const drawer = document.querySelector<HTMLElement>('#drawer');
  if (drawer?.getAttribute('aria-hidden') === 'false' && drawer.querySelector('.job-list')) renderQueue();
}

function renderQueue(): void {
  const drawer = document.querySelector<HTMLElement>('#drawer')!;
  const jobs = state.batch?.jobs || [];
  drawer.innerHTML = `<div class="drawer-head"><div><span class="eyebrow">Download centre</span><h2>${jobs.length ? `${jobs.length} chapter${jobs.length === 1 ? '' : 's'}` : 'No active exports'}</h2></div><button class="icon-button" type="button" data-action="close-drawer">×</button></div>
    <div class="job-list">${jobs.length ? jobs.map((job, index) => jobMarkup(job, index)).join('') : emptyCard('Your next export will appear here.')}</div>
    ${jobs.length && !state.batch?.running ? `<button class="secondary full" type="button" data-action="clear-jobs">Clear downloads</button>` : ''}`;
}

function jobMarkup(job: Job, index: number): string {
  const progress = job.total ? Math.round(job.done / job.total * 100) : 0;
  return `<article class="job-card"><div class="job-title"><div><strong>${esc(chapterLabel(job.chapter))}</strong><small>${esc(job.message)}</small></div><span class="job-state ${job.state}">${esc(job.state)}</span></div><div class="progress"><i style="width:${progress}%"></i></div><div class="job-footer"><span>${progress}%</span>${['resolving', 'downloading'].includes(job.state) ? `<button type="button" data-action="cancel-job" data-index="${index}">Cancel</button>` : ''}</div></article>`;
}

function openDrawer(): void {
  const drawer = document.querySelector<HTMLElement>('#drawer')!;
  const backdrop = document.querySelector<HTMLButtonElement>('#backdrop')!;
  backdrop.hidden = false;
  drawer.setAttribute('aria-hidden', 'false');
  document.body.classList.add('drawer-open');
  if (!reducedMotion()) animate(drawer, innerWidth < 760 ? { translateY: ['105%', '0%'], duration: 430, ease: 'out(4)' } : { translateX: ['105%', '0%'], duration: 430, ease: 'out(4)' });
}

function closeDrawer(immediate = false): void {
  const drawer = document.querySelector<HTMLElement>('#drawer')!;
  const backdrop = document.querySelector<HTMLButtonElement>('#backdrop')!;
  const finish = () => { drawer.setAttribute('aria-hidden', 'true'); backdrop.hidden = true; document.body.classList.remove('drawer-open'); };
  if (immediate || reducedMotion()) return finish();
  animate(drawer, innerWidth < 760 ? { translateY: ['0%', '105%'], duration: 250, ease: 'in(3)', onComplete: finish } : { translateX: ['0%', '105%'], duration: 250, ease: 'in(3)', onComplete: finish });
}

function toggleTheme(): void {
  const root = document.documentElement;
  const next = root.dataset.theme === 'light' ? 'dark' : 'light';
  root.dataset.theme = next;
  localStorage.setItem('chapterbox-theme', next);
  document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute('content', next === 'light' ? '#f6f7fb' : '#0b0d12');
  if (!reducedMotion()) animate(document.body, { opacity: [0.82, 1], duration: 260, ease: 'out(2)' });
}

function saveBlob(blob: Blob, filename: string): void {
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function animateEntrance(selector: string, delay = 55): void {
  if (reducedMotion()) return;
  createTimeline({ defaults: { duration: 560, ease: 'out(4)' } }).add(selector, { opacity: [0, 1], translateY: [18, 0], delay: stagger(delay) });
}

function shake(target: HTMLElement | null): void {
  if (!target || reducedMotion()) return;
  animate(target, { translateX: [0, -7, 7, -4, 4, 0], duration: 360, ease: 'inOut(2)' });
}

function toast(message: string): void {
  const region = document.querySelector<HTMLElement>('#toast-region')!;
  const node = document.createElement('div');
  node.className = 'toast';
  node.textContent = message;
  region.append(node);
  if (!reducedMotion()) animate(node, { opacity: [0, 1], translateY: [12, 0], scale: [0.96, 1], duration: 280, ease: 'out(4)' });
  setTimeout(() => { if (!reducedMotion()) animate(node, { opacity: 0, translateY: -8, duration: 180, onComplete: () => node.remove() }); else node.remove(); }, 3200);
}

function updateQueueCount(): void {
  const active = state.batch?.jobs.filter(job => !['complete', 'cancelled', 'failed'].includes(job.state)).length || 0;
  const count = document.querySelector<HTMLElement>('.queue-count');
  if (count) count.textContent = String(active);
}

function loadingCard(text: string): string { return `<div class="message-card">${spinner()}<span>${esc(text)}</span></div>`; }
function spinner(): string { return `<span class="spinner" aria-hidden="true"></span>`; }
function emptyCard(text: string): string { return `<div class="empty-card">${esc(text)}</div>`; }
function errorCard(text: string): string { return `<div class="error-card"><strong>Something went wrong</strong><span>${esc(text)}</span></div>`; }

async function handleClick(event: MouseEvent): Promise<void> {
  const target = (event.target as Element | null)?.closest<HTMLElement>('[data-action]');
  if (!target) return;
  const action = target.dataset.action;
  switch (action) {
    case 'home': renderHome(); break;
    case 'queue': renderQueue(); openDrawer(); break;
    case 'close-drawer': closeDrawer(); break;
    case 'open-manga': if (target.dataset.id) await openManga(target.dataset.id); break;
    case 'toggle-chapter': if (target.dataset.id) toggleChapter(target.dataset.id); break;
    case 'select-all':
      if (state.selected.size === state.chapters.length) state.selected.clear(); else state.chapters.forEach(ch => state.selected.add(ch.id));
      renderWorkspace();
      break;
    case 'load-more': await loadChapters(); renderWorkspace(); break;
    case 'download-options': openDownloadOptions(); break;
    case 'quality': state.quality = target.dataset.quality === 'data' ? 'data' : 'data-saver'; openDownloadOptions(); break;
    case 'start-download': startBatch(); break;
    case 'cancel-job': state.batch?.jobs[Number(target.dataset.index)]?.controller?.abort(); break;
    case 'clear-jobs': state.batch = null; renderQueue(); updateQueueCount(); break;
  }
}

const themeButton = document.querySelector<HTMLButtonElement>('[data-action="theme"]');
let themePointerStarted = false;
themeButton?.addEventListener('pointerdown', event => {
  themePointerStarted = event.isPrimary && event.button === 0;
});
themeButton?.addEventListener('pointercancel', () => { themePointerStarted = false; });
themeButton?.addEventListener('click', event => {
  event.preventDefault();
  event.stopPropagation();
  if (!event.isTrusted) return;
  if (event.detail > 0 && !themePointerStarted) return;
  themePointerStarted = false;
  toggleTheme();
});

document.addEventListener('click', event => { void handleClick(event); });
document.addEventListener('submit', event => {
  if ((event.target as HTMLFormElement).id === 'search-form') { event.preventDefault(); void submitSearch(); }
});
document.addEventListener('change', event => {
  const select = event.target as HTMLSelectElement;
  if (select.id === 'language-select') {
    state.language = select.value;
    state.selected.clear();
    void loadChapters(true).then(renderWorkspace).catch(error => toast((error as Error).message));
  }
});

window.addEventListener('error', event => {
  console.error('ChapterBox runtime error:', event.error || event.message);
  toast('A browser error occurred. Open DevTools Console for details.');
});
window.addEventListener('unhandledrejection', event => {
  console.error('ChapterBox promise error:', event.reason);
  toast(event.reason instanceof Error ? event.reason.message : 'An unexpected request failed.');
});

const savedTheme = localStorage.getItem('chapterbox-theme');
document.documentElement.dataset.theme = savedTheme === 'light' ? 'light' : 'dark';
shell();
renderHome();
updateQueueCount();
